import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useLocation } from "react-router-dom";
import Spinner from "components/Spinner";
import Input from "components/Input";
import Button from "components/Button";
import VerificationInputField from "components/VerificationInputField";
import ModalResetPasswordSuccessfully from "screens/ModalResetPasswordSuccessfully";
import { useMutation } from "@apollo/client";
import {
  CONFIRM_STAFF_SETUP_EMAIL,
  SEND_STAFF_SETUP_PHONE_CODE,
  VERIFY_STAFF_SETUP_PHONE,
  COMPLETE_STAFF_SETUP
} from "graphql/service";
import { Container, ContentContainer } from "./styles";
import Logo from "assets/Logo.png";
import { HiddenIcon, VisibleIcon } from "assets/icons";
import { debouncedErrorMessage } from "utils/messageUtil";
import { message } from "antd";

const SetupAccount = ({ redirectPath }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");

  const [tokenVerified, setTokenVerified] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [phoneVerificationCodeSent, setPhoneVerificationCodeSent] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationCodeError, setVerificationCodeError] = useState(false); 
  const [errorMessage, setErrorMessage] = useState("");

  const [successIsOpen, setSuccessIsOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordHidden, setPasswordHidden] = useState(true);

  const [confirmStaffSetupEmail, {data: confirmStaffSetupEmailData, loading: confirmStaffSetupEmailLoading }] = useMutation(CONFIRM_STAFF_SETUP_EMAIL, {
    onCompleted: (confirmStaffSetupEmailData) => {
      if (confirmStaffSetupEmailData.confirmStaffSetupEmail?.sessionToken) {
        setTokenVerified(true);
      }
    },
    onError: error => {
      if (!error?.networkError) {
        if (error.message.includes("expired")) {
          setTokenExpired(true);
        } else {
          setTokenInvalid(true);
        }
      }
    },
  });

  useEffect(() => {
    if (token) {
      confirmStaffSetupEmail({
        variables: {
          input: {
            token: token
          },
        },
      })
    } else {
      setTokenInvalid(true);
    }
  }, [token, confirmStaffSetupEmail]);

  const sessionToken = confirmStaffSetupEmailData?.confirmStaffSetupEmail?.sessionToken;


  const [sendStaffSetupPhoneCode, { loading: sendStaffSetupPhoneCodeLoading }] = useMutation(SEND_STAFF_SETUP_PHONE_CODE, {
    onCompleted: (data) => {
      setPhoneVerificationCodeSent(true);
      setMaskedPhone(data.sendStaffSetupPhoneCode.maskedPhone);
      message.success("Verification code sent successfully!");
    },
    onError: error => {
      if (!error?.networkError) {
        let content = error.message;
        if (error.message.includes('Validation')) {
          content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception);
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const handleSendPhoneVerificationCode = () => {
    sendStaffSetupPhoneCode({
      variables: {
        input: {
          sessionToken: sessionToken
        },
      },
    });
  };

  const [verifyStaffSetupPhone, { loading: verifyStaffSetupPhoneLoading }] = useMutation(VERIFY_STAFF_SETUP_PHONE, {
    onCompleted: (data) => {
      if (data.verifyStaffSetupPhone) {
        setPhoneVerified(true);
      }
    },
    onError: (error) => {
      setVerificationCodeError(true);
      let messages = [];

      if (error.graphQLErrors?.length > 0) {
        messages = error.graphQLErrors.map(err => err.message);
      } else if (error.networkError?.result?.errors) {
        messages = error.networkError.result.errors.map(err => err.message);
      } else if (error.networkError) {
        messages = [error.networkError.message];
      }

      if (messages?.length > 0) {
        setErrorMessage(messages.join(", "));
      }
    }
  })

  const handleVerifyPhone = () => {
    if (verificationCode.length === 6) {
      verifyStaffSetupPhone({
        variables: {
          input: {
            sessionToken: sessionToken,
            otpCode: verificationCode.join("")
          },
        },
      });
    } else {
      setVerificationCodeError(true);
    }
  };

  const [completeStaffSetup, { loading: resetPasswordLoading }] = useMutation(COMPLETE_STAFF_SETUP, {
    onCompleted: (setupData) => {
      if (setupData.completeStaffSetup) {
        setSuccessIsOpen(true);
      } else {
        const content = "Set password failed!";
        debouncedErrorMessage(content);
        setSuccessIsOpen(false);
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    },
  });

  const handleContinue = () => {
    if (password.length >= 8) {
      completeStaffSetup({
        variables: {
          input: {
            sessionToken: sessionToken,
            newPassword: password
          },
        },
      });
    } else {
      setPasswordError(true);
    }
  };

  if (confirmStaffSetupEmailLoading) {
    return <Spinner />;
  }

  return (
    <Container>
      <Helmet>
        <title>Setup - Rook Hotel PMS</title>
        <meta name="description" content="Setup - Rook Hotel PMS" />
      </Helmet>
      <ContentContainer>
        <img src={Logo} alt="Logo" />
        <div className="brand">
          <span className="rook">ROOK </span>
          <span className="hotel">HOTEL</span>
        </div>
        {phoneVerified ? (
          <div className="infoContainer">
            <div className="title">Setup Password</div>
            <div className="setupPassword">
              {/* Dummy input to catch unwanted email autofill */}
              <input 
                type="text" 
                name="email"
                tabIndex={-1}
                autoComplete="username"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '1px',
                  height: '1px',
                  opacity: 0,
                  overflow: 'hidden',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  pointerEvents: 'none'
                }}
              />
              <Input
                title={"NEW PASSWORD"}
                required
                error={passwordError}
                errorMessage={
                  password.length
                    ? password.length < 8 ? "Password must contain at least 8 characters.": ""
                    : "This field is required."
                }
                placeholder={"Password"}
                autoComplete="new-password"
                onChange={(e) => {
                  setPasswordError(false);
                  setPassword(e.target.value);
                }}
                type={passwordHidden ? "password" : "text"}
                additionalstyle={`
                  width: 100%;
                  margin-top: 24px;
                  input {
                    font-size: 14px;
                    font-weight: 400;
                  }
                `}
              />
              {passwordHidden ? (
                <div
                  className="passwordLock"
                  onClick={() => {
                    setPasswordHidden(false);
                  }}
                >
                  <HiddenIcon />
                </div>
              ) : (
                <div
                  className="passwordUnlock"
                  onClick={() => {
                    setPasswordHidden(true);
                  }}
                >
                  <VisibleIcon />
                </div>
              )}
            </div>
            <Button
              mainPage
              onClick={handleContinue}
              data-loading={resetPasswordLoading}
              additionalstyle={`
                width: 97px;
                margin-top: 24px;
              `}
            >
              {resetPasswordLoading ? null : "Continue"}
            </Button>
          </div>
        ): phoneVerificationCodeSent ? (
          <div className="infoContainer">
            <div className="title">Verify Your Phone</div>
            <div className="description">
              {`We’ve sent a 6‑digit code to: ${maskedPhone}.`}
            </div>
            <VerificationInputField
              label="ENTER VERIFICATION CODE"
              required
              width="270px"
              labelPosition='center'
              length={6}
              values={verificationCode}
              setValues={(values) => {
                setVerificationCode(values)
                setVerificationCodeError(false)
                setErrorMessage("")
              }}
              error={verificationCodeError}
              errorMessage={errorMessage}
              margin="24px 0 16px 0"
            />
            <div className="description">
              Didn’t get the code? 
              <span
                className="highlight"
                onClick={handleSendPhoneVerificationCode}
              >
                Resend
              </span>
            </div>
            <Button
              mainPage
              onClick={handleVerifyPhone}
              data-loading={verifyStaffSetupPhoneLoading}
              additionalstyle={`
                width: 124px;
                margin-top: 24px;
              `}
            >
              {verifyStaffSetupPhoneLoading ? null : "Verify Phone"}
            </Button>
          </div>
        ): tokenVerified ? (
          <div className="infoContainer">
            <div className="title">Your email has been verified!</div>
            <div className="description">
              To finish setting up your account, <br></br>
              we need to verify your phone number.
            </div>
            <Button
              mainPage
              onClick={handleSendPhoneVerificationCode}
              data-loading={sendStaffSetupPhoneCodeLoading}
              additionalstyle={`
                width: 97px;
                margin-top: 24px;
              `}
            >
              {sendStaffSetupPhoneCodeLoading ? null : "Continue"}
            </Button>
          </div>
        ): tokenExpired ? (
          <div className="infoContainer">
            <div className="title warning">This link has expired.</div>
            <div className="description">
              We sent you a new one, check your email. If you don’t receive it within a few minutes, please contact your administrator for assistance.
            </div>
          </div>
        ): tokenInvalid ? (
          <div className="infoContainer">
            <div className="title warning">This link is invalid.</div>
            <div className="description">
              The link you clicked is invalid. Please contact your administrator for a new invitation email.
            </div>
          </div>
        ): null}
      </ContentContainer>
      <ModalResetPasswordSuccessfully
        isOpen={successIsOpen}
        setClose={() => {
          setSuccessIsOpen(false);
          navigate("/");
        }}
      />
    </Container>
  );
};

export default SetupAccount;
